import { Component, Inject } from '@angular/core';
import {
  GridDataResult,
  DataStateChangeEvent,
} from '@progress/kendo-angular-grid';
import {
  ColumnOptions,
  NamedEntity,
  MessageService,
  LoaderService,
  GridState,
  GridStateCompositeFilterDescriptor,
  GridRowType,
} from '@shared';
import { TasksService } from '@admin';
import {
  ChangesetService,
  ReportingUnit,
  RestaurantService,
  Restaurant,
} from '@shared-data';
import { TokenService } from '@auth';
import { GridRowAvailabilityChangeset, GridRowChangeset, GridRowRealignmentChangeset } from '../../models';
import { GroupDescriptor } from '@progress/kendo-data-query';
import { CHANGESET_STRATEGIES, ChangesetType } from '@restaurant-item';

@Component({
  selector: 'app-failures',
  templateUrl: './failures.component.html',
  styleUrl: './failures.component.scss',
  providers: [
    {
      provide: CHANGESET_STRATEGIES,
      useValue: {
        pricing: new GridRowChangeset(),
        availability: new GridRowAvailabilityChangeset(), 
        realignment: new GridRowRealignmentChangeset(),
      },
    },
  ],
})
export class FailuresComponent {
  changesets: GridDataResult = { data: [], total: 0 };

  group: GroupDescriptor[] = [{ field: 'restaurantName', dir: 'asc' }];

  pageSize = 10;
  state = new GridState(this.pageSize);

  columns: ColumnOptions[] = [];
  types: NamedEntity[] = [
    new NamedEntity('pricing', 'Pricing'),
    new NamedEntity('availability', 'Availability'),
    new NamedEntity('realignment', 'Realignment'),
  ];

  _selectedType: NamedEntity = this.types[0];
  get selectedType(): NamedEntity {
    return this._selectedType;
  }

  set selectedType(value: NamedEntity) {
    this._selectedType = value;
    this.toggleColumnVisibility();
    this.setDefaultState();
  }


  isAvailabilityOnly: boolean = false;

  fontSize: number = 0.875;

  selectedReportingUnit?: ReportingUnit = undefined;
  defaultReportingUnit: ReportingUnit = {
    ruNo: -1,
    ruNm: 'All Reporting Units',
  };

  reportingUnits: GridDataResult = { data: [], total: 0 };

  selectedRestaurant?: Restaurant = undefined;
  defaultRestaurant: Restaurant = new Restaurant('-1', 'All Restaurants');

  restaurants: Restaurant[] = [];

    constructor(@Inject(CHANGESET_STRATEGIES)
    private strategies: Record<ChangesetType, GridRowType>,
    private changesetService: ChangesetService,
    private messageService: MessageService,
    private loaderService: LoaderService,
    private restaurantService: RestaurantService,
    private tasksService: TasksService,
    private tokenService: TokenService,
  ) {
  }

  async ngOnInit() {
    this.isAvailabilityOnly = await this.tokenService.isAvailabilityOnly();

    // For AvailabilityOnly users, filter types to only show Availability
    if (this.isAvailabilityOnly) {
      this.types = this.types.filter((type) => type.id === 'availability');
      this.selectedType = this.types[0];
    }

    this.fetchReportingUnits();
  }

  fetchReportingUnits(): void {
    const state = new GridState();
    this.loaderService.show();
    this.restaurantService.getReportingUnits(state).subscribe({
      next: (result: GridDataResult) => {
        this.reportingUnits = result;
        this.selectedReportingUnit = this.defaultReportingUnit;
        this.fetchRestaurants();
        this.loaderService.hide();
      },
      error: () => {
        this.messageService.error('Failed to load reporting units');
        this.loaderService.hide();
      },
    });
  }

  fetchRestaurants(): void {
    this.loaderService.show();
    this.restaurantService
      .getParTokenRestaurants(this.selectedReportingUnit?.ruNo)
      .subscribe({
        next: (result: Restaurant[]) => {
          this.restaurants = result;

          this.selectedRestaurant = this.defaultRestaurant;

          this.setDefaultState();
          this.loaderService.hide();
        },
        error: () => {
          this.messageService.error('Failed to load restaurants');
          this.loaderService.hide();
        },
      });
  }

  onDataStateChanged(event: DataStateChangeEvent) {
    this.fetchChangesets(event as GridState);
  }

  setDefaultState() {
    this.state = new GridState(GridState.getPageSizeByFontSize(this.fontSize));

    this.fetchChangesets(this.state);
  }

  fetchChangesets(state: GridState) {
    this.loaderService.show();
    if (state.filter !== undefined) {
      this.state.filter = state.filter;
    }
    if (state.skip !== undefined) {
      this.state.skip = state.skip;
    }
    if (state.take !== undefined) {
      this.state.take = state.take;
    }
    if (state.sort?.length) {
      this.state.sort = state.sort;
    } else {
      this.state.sort = [{ field: 'scheduledDate', dir: 'desc' }];
    }

    this.state.filter = new GridStateCompositeFilterDescriptor(
      this.state.filter,
    )?.and({
      field: 'type',
      operator: 'eq',
      value: this.selectedType.id,
    });

    this.state.group = this.group;

    this.changesetService
      .getFailedChangesets(
        this.state,
        this.selectedRestaurant?.id != '-1'
          ? this.selectedRestaurant?.id
          : undefined,
        this.selectedReportingUnit?.ruNo != -1
          ? this.selectedReportingUnit?.ruNo
          : undefined,
      )
      .subscribe({
        next: (changesets: GridDataResult) => {
          this.toggleColumnVisibility();

          this.changesets = changesets;

          this.loaderService.hide();
        },
        error: () => {
          this.messageService.error('Failed to load changesets');
          this.loaderService.hide();
        },
      });
  }

  toggleColumnVisibility() {
    if (this.selectedType) {
      const strat = this.strategies?.[this.selectedType.id as ChangesetType];
      if (!strat) return;
      this.columns = strat
        .getColumns()
        .filter((c: ColumnOptions) => c.visibleInQuickView);
    }
  }

  refreshRestaurant(restaurantName: string) {
    this.invokeTask(restaurantName, '/itemsv2');
    this.invokeTask(restaurantName, '/pricechange');
  }

  invokeTask(restaurantName: string, endpoint: string) {
    this.loaderService.show();
    this.tasksService
      .invokeAPI({
        apiName: endpoint,
        restaurantNumbers: [restaurantName],
        syncOption: 'Specific',
      })
      .subscribe({
        next: () => {
          this.loaderService.hide();
          this.messageService.success(
            `Task: ${endpoint} sent to queue: ${restaurantName}`,
          );
        },
        error: (err: any) => {
          this.messageService.error(`Failed to invoke task api: ${err.error}`);
          this.loaderService.hide();
        },
      });
  }
}
